<template>
  <div id="app" :class="darkMode ? 'dark' : ''">
    <!-- 移动端侧栏图标 -->
    <div v-if="showSidebar && isMobile" class="fixed top-4 left-4 z-50">
      <button @click="toggleSidebar"
        :class="[
          'flex items-center justify-center w-10 h-10 border rounded-lg shadow-lg transition-colors',
          darkMode 
            ? 'bg-[#111722] border-slate-800 text-white hover:bg-[#232f48]'
            : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
        ]">
        <span class="material-symbols-outlined" style="font-size: 24px;">
          {{ sidebarExpanded ? 'close' : 'menu' }}
        </span>
      </button>
    </div>

    <div v-if="showSidebar" class="flex h-screen w-full">
      <!-- 侧边栏 -->
      <aside :class="[
        'flex-shrink-0 transition-all duration-300 ease-in-out',
        isMobile ? (sidebarExpanded ? 'w-full fixed inset-y-0 left-0 z-40 overflow-y-auto' : 'w-0 overflow-hidden') : 'w-52',
        darkMode ? 'bg-background-dark border-r border-slate-700' : 'bg-white border-r border-slate-200'
      ]">
        <div class="flex flex-col justify-between p-4" :class="isMobile && sidebarExpanded ? 'min-h-screen' : 'h-full min-h-[700px]'">
          <div class="flex flex-col gap-4">
            <!-- 用户信息 -->
            <div class="flex flex-col gap-2">
              <label class="flex gap-3 items-center p-2 -ml-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-all group" for="switch-stream-modal" @click="openStreamerSelector" :style="isMobile ? 'margin-top: 40px' : ''">
                <div class="relative">
                  <div class="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 ring-2 ring-transparent group-hover:ring-primary/20 transition-all"
                    data-alt="Streamer's profile picture" :style="{ backgroundImage: 'url(' + streamerAvatar + ')' }">
                  </div>
                  <div class="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full p-0.5 shadow-sm">
                    <span class="material-symbols-outlined text-slate-400 dark:text-slate-400 leading-none" style="font-size: 12px !important;">sync_alt</span>
                  </div>
                </div>
                <div class="flex flex-col">
                  <div class="flex items-center gap-1">
                    <h1 class="text-slate-900 dark:text-slate-100 text-base font-medium leading-normal group-hover:text-primary transition-colors">{{ streamerName }}</h1>
                  </div>
                  <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-normal">{{ userStatusText }}</p>
                </div>
              </label>
              
            </div>

            <!-- 导航菜单 -->
            <div class="flex flex-col gap-2">
              <router-link to="/on-air"
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-lg',
                  $route.name === 'OnAir'
                    ? darkMode 
                      ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20 text-white shadow-sm shadow-primary/5'
                      : 'bg-primary hover:bg-[#009e65] text-white shadow-sm shadow-primary/30'
                    : darkMode
                      ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                ]" @click.native="handleNavigationClick">
                <span class="material-symbols-outlined"
                  :style="{
                    fontSize: '24px',
                    fontVariationSettings: $route.name === 'OnAir' ? '\'FILL\' 1' : '\'FILL\' 0'
                  }">live_tv</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.onAir') }}</p>
              </router-link>
              <router-link to="/analytics"
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-lg',
                  $route.name === 'Analytics'
                    ? darkMode 
                      ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20 text-white shadow-sm shadow-primary/5'
                      : 'bg-primary hover:bg-[#009e65] text-white shadow-sm shadow-primary/30'
                    : darkMode
                      ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                ]" @click.native="handleNavigationClick">
                <span class="material-symbols-outlined" :style="{
                  fontSize: '24px',
                  fontVariationSettings: $route.name === 'Analytics' ? '\'FILL\' 1' : '\'FILL\' 0'
                }">bar_chart</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.analytics') }}</p>
              </router-link>
              <router-link to="/settings" :class="[
                'flex items-center gap-3 px-3 py-2 rounded-lg',
                $route.name === 'Settings'
                  ? darkMode 
                    ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20 text-white shadow-sm shadow-primary/5'
                    : 'bg-primary hover:bg-[#009e65] text-white shadow-sm shadow-primary/30'
                  : darkMode
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              ]" @click.native="handleNavigationClick">
                <span class="material-symbols-outlined" :style="{
                  fontSize: '24px',
                  fontVariationSettings: $route.name === 'Settings' ? '\'FILL\' 1' : '\'FILL\' 0'
                }">toggle_on</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.settings') }}</p>
              </router-link>
              <router-link to="/history" :class="[
                'flex items-center gap-3 px-3 py-2 rounded-lg',
                $route.name === 'History'
                  ? darkMode 
                    ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20 text-white shadow-sm shadow-primary/5'
                    : 'bg-primary hover:bg-[#009e65] text-white shadow-sm shadow-primary/30'
                  : darkMode
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              ]" @click.native="handleNavigationClick">
                <span class="material-symbols-outlined" :style="{
                  fontSize: '24px',
                  fontVariationSettings: $route.name === 'History' ? '\'FILL\' 1' : '\'FILL\' 0'
                }">history</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.history') }}</p>
              </router-link>
            </div>
          </div>

          <!-- 底部区域 -->
          <div class="flex flex-col gap-4">
            <!-- 直播统计数据 - 只在直播状态显示 -->
            <div v-if="isLive" :class="[
              'flex flex-col gap-4 p-3 rounded-lg border',
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
            ]">
              <p :class="[
                'text-xs font-semibold uppercase tracking-wider',
                darkMode ? 'text-slate-400' : 'text-slate-500'
              ]">{{ $t('app.liveStreamBasicInfo') }}</p>
              <div class="flex flex-col gap-3 text-sm">
                <div class="relative">
                  <div class="bg-center bg-no-repeat aspect-video bg-cover rounded w-full"
                    :style="{ backgroundImage: 'url(' + liveStreamCover + ')' }"></div>
                  <div
                    class="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600/80 backdrop-blur-sm text-white px-2 py-0.5 rounded-md text-xs font-bold">
                    <span class="relative flex h-2 w-2">
                      <span
                        class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                    <span>{{ $t('app.live') }}</span>
                  </div>
                </div>
                <div class="flex flex-col">
                  <p :class="[
                    'font-medium',
                    darkMode ? 'text-white' : 'text-slate-900'
                  ]">{{ liveStreamTitle }}</p>
                  <p :class="[
                    'text-xs',
                    darkMode ? 'text-slate-400' : 'text-slate-500'
                  ]">{{ $t('app.liveRoomTitle') }}</p>
                </div>
                <div class="flex items-center justify-between">
                  <span :class="darkMode ? 'text-slate-400' : 'text-slate-500'">{{ $t('app.duration') }}</span>
                  <span :class="[
                    'font-medium',
                    darkMode ? 'text-white' : 'text-slate-900'
                  ]">{{ liveStreamDuration }}</span>
                </div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div class="flex items-center gap-1.5" :class="darkMode ? 'text-slate-400' : 'text-slate-500'">
                    <span class="material-symbols-outlined" style="font-size: 16px;">visibility</span>
                    <span :class="[
                      'font-medium',
                      darkMode ? 'text-white' : 'text-slate-800'
                    ]">{{ liveStreamViewers }}</span>
                  </div>
                  <div class="flex items-center gap-1.5" :class="darkMode ? 'text-slate-400' : 'text-slate-500'">
                    <span class="material-symbols-outlined" style="font-size: 16px;">favorite</span>
                    <span :class="[
                      'font-medium',
                      darkMode ? 'text-white' : 'text-slate-800'
                    ]">{{ liveStreamLikes }}</span>
                  </div>
                  <div class="flex items-center gap-1.5" :class="darkMode ? 'text-slate-400' : 'text-slate-500'">
                    <span class="material-symbols-outlined" style="font-size: 16px;">chat_bubble</span>
                    <span :class="[
                      'font-medium',
                      darkMode ? 'text-white' : 'text-slate-800'
                    ]">{{ liveStreamComments }}</span>
                  </div>
                  <div class="flex items-center gap-1.5" :class="darkMode ? 'text-slate-400' : 'text-slate-500'">
                    <span class="material-symbols-outlined" style="font-size: 16px;">monetization_on</span>
                    <span :class="[
                      'font-medium',
                      darkMode ? 'text-white' : 'text-slate-800'
                    ]">{{ liveStreamRevenue }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 账户余额 -->
            <div :class="[
              'flex flex-col gap-2 p-3 rounded-lg border',
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
            ]">
              <p :class="[
                'text-xs font-semibold uppercase tracking-wider',
                darkMode ? 'text-slate-400' : 'text-slate-500'
              ]">{{ $t('app.followUpAccountBalance') }}</p>
              <div class="mt-2">
                <div v-if="benefitLoading" class="flex justify-center items-center py-2">
                  <span :class="darkMode ? 'text-slate-400' : 'text-slate-500'">{{ $t('app.loading') }}</span>
                </div>
                <div v-else-if="accountBenefitInfo && accountBenefitInfo.success" class="space-y-3">
                  <!-- 剩余时长进度条 -->
                  <div>
                    <div class="flex justify-between items-center text-xs mb-1">
                      <span :class="darkMode ? 'text-slate-300' : 'text-slate-600'">{{ $t('app.remainingDuration') }}:</span>
                      <span :class="[
                        'font-medium',
                        darkMode ? 'text-white' : 'text-slate-900'
                      ]">{{
                        formatDurationHours(accountBenefitInfo.data.followDurationBenefit.remainingDuration) }}</span>
                    </div>
                    <div :class="[
                      'w-full rounded-full h-1.5',
                      darkMode ? 'bg-slate-700' : 'bg-slate-200'
                    ]">
                      <div class="bg-green-500 h-1.5 rounded-full"
                        :style="{ width: getDurationPercentage(accountBenefitInfo.data.followDurationBenefit) + '%' }">
                      </div>
                    </div>
                  </div>

                  <!-- 话术token进度条 -->
                  <div>
                    <div class="flex justify-between items-center text-xs mb-1">
                      <span :class="darkMode ? 'text-slate-300' : 'text-slate-600'">{{ $t('app.scriptTokens') }}:</span>
                      <span :class="[
                        'font-medium',
                        darkMode ? 'text-white' : 'text-slate-900'
                      ]">{{
                        formatTokens(accountBenefitInfo.data.scriptTokenBenefit.remainingTokens) }}</span>
                    </div>
                    <div :class="[
                      'w-full rounded-full h-1.5',
                      darkMode ? 'bg-slate-700' : 'bg-slate-200'
                    ]">
                      <div class="bg-blue-500 h-1.5 rounded-full"
                        :style="{ width: getTokenPercentage(accountBenefitInfo.data.scriptTokenBenefit) + '%' }"></div>
                    </div>
                  </div>

                  <!-- 视频分析token进度条 -->
                  <div>
                    <div class="flex justify-between items-center text-xs mb-1">
                      <span :class="darkMode ? 'text-slate-300' : 'text-slate-600'">{{ $t('app.videoAnalysisTokens') }}:</span>
                      <span :class="[
                        'font-medium',
                        darkMode ? 'text-white' : 'text-slate-900'
                      ]">{{
                        formatTokens(accountBenefitInfo.data.videoAnalysisTokenBenefit.remainingTokens) }}</span>
                    </div>
                    <div :class="[
                      'w-full rounded-full h-1.5',
                      darkMode ? 'bg-slate-700' : 'bg-slate-200'
                    ]">
                      <div class="bg-purple-500 h-1.5 rounded-full"
                        :style="{ width: getTokenPercentage(accountBenefitInfo.data.videoAnalysisTokenBenefit) + '%' }">
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="flex justify-center items-center py-2">
                  <span :class="darkMode ? 'text-slate-400' : 'text-slate-500'">{{ $t('app.noStreamers') }}</span>
                </div>
              </div>
            </div>

            <!-- 帮助和退出 -->
            <div class="flex flex-col gap-1">
              <div
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
                  darkMode 
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                ]">
                <span class="material-symbols-outlined" style="font-size: 24px;">help</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.help') }}</p>
              </div>
              <div
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
                  darkMode 
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                ]"
                @click="handleLogout">
                <span class="material-symbols-outlined" style="font-size: 24px;">logout</span>
                <p class="text-sm font-medium leading-normal">{{ $t('navigation.logout') }}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- 移动端遮罩层 -->
      <div v-if="isMobile && sidebarExpanded"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden"
        @click="toggleSidebar">
      </div>

      <!-- 主内容区域 -->
      <main :class="[
        'grid grid-cols-12 grid-rows-1 gap-6 overflow-hidden transition-all duration-300  bg-background-light dark:bg-background-dark',
        isMobile ? 'flex-1 p-4' : 'flex-1 p-0'
      ]">
        <router-view></router-view>
      </main>
    </div>

    <!-- 无侧边栏布局 -->
    <div v-else class="h-screen w-full">
      <router-view></router-view>
    </div>

    <!-- 主播选择模态框 -->
    <div v-if="showStreamerSelector" class="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="relative w-full max-w-6xl max-h-[90vh] flex flex-col bg-background-light dark:bg-background-dark rounded-2xl shadow-2xl overflow-hidden group/design-root border border-transparent dark:border-gray-800">
        <div class="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-surface-light dark:bg-surface-dark z-10 shrink-0">
          <div>
            <h2 class="text-[#0d1b12] dark:text-white text-2xl font-bold leading-tight tracking-[-0.015em]">{{ $t('app.switchLiveStream') }}</h2>
            <p class="text-gray-500 dark:text-gray-400 text-sm mt-1">{{ $t('app.manageAndSwitchChannels') }}</p>
            <p v-if="pagination.total > 0" class="text-gray-500 dark:text-gray-300 font-bold text-sm mt-2">{{ $t('app.totalStreamsTracked', { count: pagination.total }) }}</p>
          </div>
          <button @click="showStreamerSelector = false" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400">
            <span class="material-symbols-outlined text-[24px]">close</span>
          </button>
        </div>
        
        <!-- 搜索框 - 始终显示 -->
        <div class="px-6 py-4 bg-surface-light dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800">
          <div class="flex flex-col md:flex-row items-center gap-3">
            <label class="flex flex-col w-full md:w-1/2 lg:w-1/3 h-12">
              <div class="flex w-full flex-1 items-stretch rounded-xl h-full shadow-sm ring-1 ring-gray-200 dark:ring-gray-700 focus-within:ring-2 focus-within:ring-primary transition-shadow overflow-hidden bg-white dark:bg-[#1a1a1a]">
                <div class="text-gray-400 dark:text-gray-200 flex border-none items-center justify-center pl-4 pr-2 bg-transparent">
                  <span class="material-symbols-outlined text-[20px]">search</span>
                </div>
                <input v-model="searchQuery" @keyup.enter="searchStreamers" class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#0d1b12] dark:text-white focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 dark:placeholder:text-gray-500 px-0 text-base font-normal leading-normal" :placeholder="$t('app.searchStreamers')" />
              </div>
            </label>
            <button @click="searchStreamers" class="h-12 px-4 rounded-xl bg-primary hover:bg-[#00b058] text-white text-sm font-bold transition-colors shadow-sm shadow-primary/30 flex items-center gap-2">
              <span class="material-symbols-outlined text-[18px]">search</span>
              <span>{{ $t('common.search') }}</span>
            </button>
            <button v-if="searchQuery" @click="clearSearch" class="h-12 px-4 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-bold transition-colors flex items-center gap-2">
              <span class="material-symbols-outlined text-[18px]">close</span>
              <span>{{ $t('common.clear') }}</span>
            </button>
          </div>
        </div>
        
        <!-- 可滚动的内容区域 -->
        <div class="flex-1 overflow-y-auto bg-[#f8fcf9] dark:bg-background-dark">
          <div v-if="streamersLoading" class="flex justify-center items-center py-12">
            <div class="flex flex-col items-center gap-3">
              <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
              </svg>
              <span class="text-text-muted text-sm">{{ $t('common.loading') }}</span>
            </div>
          </div>
          
          <div v-else-if="streamers.length === 0" class="flex flex-col items-center justify-center p-10 min-h-[500px]">
            <div class="flex flex-col items-center justify-center max-w-2xl text-center space-y-8">
              <div class="h-24 w-24 rounded-full bg-primary/10 dark:bg-surface-dark border border-transparent dark:border-gray-800 flex items-center justify-center animate-pulse">
                <span class="material-symbols-outlined text-[48px] text-primary">smart_toy</span>
              </div>
              <h3 class="text-xl md:text-2xl font-bold text-[#0d1b12] dark:text-white leading-relaxed max-w-lg">
                {{ $t('app.addLiveTrackingAgent') }}
              </h3>
              <!-- <button @click="goToOnboarding" class="px-8 h-12 rounded-full bg-primary hover:bg-[#00b058] text-white text-sm font-bold transition-all shadow-md shadow-primary/20 flex items-center gap-2 transform active:scale-95">
                <span class="material-symbols-outlined text-[20px]">add_circle</span>
                <span>Track live stream</span>
              </button> -->
            </div>
          </div>
          
          <div v-else class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div v-for="streamer in filteredStreamers" :key="streamer.id" 
                   class="bg-surface-light dark:bg-surface-dark rounded-xl p-5 border border-gray-200 dark:border-gray-700 hover:border-primary/50 transition-colors relative flex flex-col gap-4 shadow-sm"
                   :class="streamer.id === currentStreamerId ? 'border-2 border-primary shadow-primary/10' : ''">
                <div class="flex items-start gap-3">
                  <div class="relative flex-shrink-0">
                    <div class="bg-center bg-no-repeat bg-cover rounded-full h-14 w-14 ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-surface-dark"
                         :style="{ backgroundImage: 'url(' + streamer.avatar + ')' }">
                    </div>
                    <div v-if="streamer.isLive" class="absolute -bottom-1 -right-1 bg-primary text-[#0d1b12] text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
                      {{ $t('app.live') }}
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-2">
                      <div class="flex-1 min-w-0">
                        <h3 class="text-[#0d1b12] dark:text-white text-lg font-bold leading-tight truncate">{{ streamer.name }}</h3>
                        <p class="text-gray-500 dark:text-gray-400 text-sm font-medium truncate">{{ streamer.username || '@' + streamer.name.toLowerCase() }}</p>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="flex items-center gap-1 px-2 py-1 rounded-md whitespace-nowrap"
                             :class="streamer.isLive ? 'text-primary bg-primary/10' : 'text-gray-400 bg-gray-100 dark:bg-gray-800'">
                          <span class="material-symbols-outlined text-[16px]"
                            :class="streamer.isLive ? 'animate-pulse' : ''">fiber_manual_record</span>
                          <span class="text-xs font-bold tracking-wide">{{ streamer.isLive ? $t('app.onAir') : $t('app.offline') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-4 py-2 border-b border-gray-100 dark:border-gray-800">
                  <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <span class="material-symbols-outlined text-[18px]">group</span>
                    <span class="text-xs font-semibold dark:text-gray-300">{{ streamer.followers || '0' }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <span class="material-symbols-outlined text-[18px]">video_library</span>
                    <span class="text-xs font-semibold dark:text-gray-300">{{ streamer.videos || '0' }}</span>
                  </div>
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span class="material-symbols-outlined text-[16px]">history</span>
                    <span>{{ $t('app.last') }}: {{ streamer.lastLive || $t('app.never') }}</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span class="material-symbols-outlined text-[16px]">timer</span>
                    <span>{{ $t('app.duration') }}: {{ streamer.duration || '0m' }}</span>
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 mt-1">
                  <span v-for="tag in streamer.tags" :key="tag"
                    class="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium">
                    {{ tag }}
                  </span>
                </div>

                <div class="mt-auto pt-2">
                  <button v-if="streamer.id === currentStreamerId"
                    class="w-full flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-primary text-white text-sm font-bold cursor-default shadow-sm shadow-primary/30"
                    disabled>
                    <span class="material-symbols-outlined text-[18px]">check_circle</span>
                    <span>{{ $t('app.currentStream') }}</span>
                  </button>
                  <button v-else-if="isStreamerClickable(streamer)" @click="selectStreamer(streamer)"
                    class="w-full flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-primary hover:bg-[#00b058] text-white text-sm font-bold leading-normal tracking-[0.015em] transition-colors shadow-sm shadow-primary/30">
                    <span>{{ $t('app.switch') }}</span>
                  </button>
                  <button v-else
                    class="w-full flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm font-bold cursor-not-allowed"
                    disabled>
                    <span class="material-symbols-outlined text-[18px]">block</span>
                    <span>{{ $t('app.notAvailable') }}</span>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- 分页组件 -->
            <div v-if="pagination.totalPages > 1" class="mt-8 flex flex-col items-center justify-center space-y-4">
              <div class="flex items-center space-x-2">
                <!-- 上一页按钮 -->
                <button 
                  @click="goToPage(pagination.currentPage - 1)"
                  :disabled="pagination.currentPage === 1"
                  :class="[
                    'flex items-center justify-center w-10 h-10 rounded-lg border transition-colors',
                    pagination.currentPage === 1
                      ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary/50'
                  ]">
                  <span class="material-symbols-outlined text-[20px]">chevron_left</span>
                </button>
                
                <!-- 页码按钮 -->
                <div class="flex items-center space-x-1">
                  <button 
                    v-for="page in getVisiblePages()" 
                    :key="page"
                    @click="goToPage(page)"
                    :class="[
                      'flex items-center justify-center w-10 h-10 rounded-lg border transition-colors',
                      page === pagination.currentPage
                        ? 'bg-primary border-primary text-white shadow-sm shadow-primary/30'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary/50'
                    ]">
                    {{ page }}
                  </button>
                  
                  <!-- 省略号 -->
                  <span v-if="pagination.totalPages > 7 && pagination.currentPage < pagination.totalPages - 3" 
                    class="flex items-center justify-center w-10 h-10 text-gray-500 dark:text-gray-400">
                    ...
                  </span>
                </div>
                
                <!-- 下一页按钮 -->
                <button 
                  @click="goToPage(pagination.currentPage + 1)"
                  :disabled="pagination.currentPage === pagination.totalPages"
                  :class="[
                    'flex items-center justify-center w-10 h-10 rounded-lg border transition-colors',
                    pagination.currentPage === pagination.totalPages
                      ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary/50'
                  ]">
                  <span class="material-symbols-outlined text-[20px]">chevron_right</span>
                </button>
              </div>
              
              <!-- 分页信息 -->
              <div class="text-sm text-gray-600 dark:text-gray-400">
                {{ $t('app.pageInfo', {
                  start: (pagination.currentPage - 1) * pagination.pageSize + 1,
                  end: Math.min(pagination.currentPage * pagination.pageSize, pagination.total),
                  total: pagination.total
                }) }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- 固定的底部区域 - Track more按钮 -->
        <div class="p-6 bg-surface-light dark:bg-surface-dark border-t border-gray-100 dark:border-gray-800 z-10 shrink-0">
          <div class="flex justify-center">
              <button @click="goToOnboarding"
                class="w-auto px-5 flex items-center justify-center gap-2 h-9 rounded-full bg-primary hover:bg-[#00b058] text-white text-sm font-bold transition-all shadow-md shadow-primary/20">
                <span class="material-symbols-outlined text-[18px]">add_circle</span>
                <span>{{ $t('app.trackMore') }}</span>
              </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/services/authService'
import urlUtils from '@/utils/urlUtils'
import { getApiUrl } from '@/config/apiConfig';

export default {
  name: 'App',
  data() {
    return {
      // 深色/浅色模式
      darkMode: false,
      // 直播状态数据
      isLive: false,
      streamerName: '',
      streamerAvatar: '/avatar_default.png',
      liveStreamCover: '/cover_default.png',
      liveStreamTitle: '',
      liveStreamDuration: '00:00:00',
      liveStreamViewers: '0',
      liveStreamLikes: '0',
      liveStreamComments: '0',
      liveStreamRevenue: '$0.00',
      remainingDuration: '0',
      remainingDurationPercentage: 0,
      // 用于接收子组件传递的直播数据
      liveSessionData: null,
      // 直播时长递增相关数据
      initialLiveDuration: 0, // 初始时长（秒）
      currentLiveDuration: 0, // 当前时长（秒）
      durationInterval: null, // 时长递增定时器
      // 直播统计数据定时更新相关
      liveStatsInterval: null, // 直播统计数据定时器
      // 账户权益信息
      accountBenefitInfo: null,
      benefitLoading: false,
      // 主播选择相关数据
      showStreamerSelector: false,
      currentStreamerId: null,
      streamers: [],
      streamersLoading: false,
      searchQuery: '',
      // 分页相关数据
      pagination: {
        currentPage: 1,
        pageSize: 12, // 每页显示12个主播
        total: 0,
        totalPages: 0
      },
      // 响应式侧栏相关
      sidebarExpanded: true, // 侧栏展开状态
      windowWidth: 0 // 窗口宽度
    }
  },
  created() {
    // 初始化token迁移（从cookie到localStorage）
    authService.initializeToken();

    // 检查是否是登录回调
    if (urlUtils.isLoginCallback()) {
      const token = urlUtils.handleLoginCallback();
      if (token) {
        authService.setToken(token);
        // 跳转到首页
        this.$router.push('/');
      }
    }

    // 检查登录状态
    this.checkAuthStatus();

    // 获取账户权益信息
    this.fetchAccountBenefitInfo();

    // 从缓存加载当前主播信息
    this.loadCurrentStreamerFromCache();

    // 初始化深色/浅色模式
    this.initDarkMode();
  },
  mounted() {
    // 初始化窗口宽度
    this.windowWidth = window.innerWidth;
    // 根据初始窗口大小设置侧栏状态
    this.updateSidebarForMobile();

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleWindowResize);
    
    // 监听主题模式变化事件
    window.addEventListener('themeModeChanged', this.handleThemeModeChanged);
  },
  beforeDestroy() {
    // 移除窗口大小监听器
    window.removeEventListener('resize', this.handleWindowResize);
    
    // 移除主题模式变化事件监听
    window.removeEventListener('themeModeChanged', this.handleThemeModeChanged);

    // 组件销毁时清除定时器
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }

    // 清除直播统计数据定时器
    this.stopLiveStatsTimer();
  },
  computed: {
    showSidebar() {
      // 只在OnAir、Analytics、AnalyticsSingle、Settings、History页面显示侧边栏
      const sidebarRoutes = ['OnAir', 'Analytics', 'AnalyticsSingle', 'Settings', 'History'];
      return sidebarRoutes.includes(this.$route.name);
    },
    // 计算用户状态文本
    userStatusText() {
      return this.isLive ? this.$t('app.liveOnTikTok') : this.$t('app.readyToGoLive');
    },
    // 检测是否为移动端（宽度小于768px）
    isMobile() {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 768;
      }
      return false;
    },
    // 过滤主播列表
    filteredStreamers() {
      if (!this.searchQuery.trim()) {
        return this.streamers;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.streamers.filter(streamer => 
        streamer.name.toLowerCase().includes(query) ||
        (streamer.username && streamer.username.toLowerCase().includes(query)) ||
        (streamer.roomId && streamer.roomId.toLowerCase().includes(query))
      );
    }
  },
  methods: {
    // 处理窗口大小变化
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
      this.updateSidebarForMobile();
    },
    // 根据屏幕宽度更新侧栏状态
    updateSidebarForMobile() {
      if (this.windowWidth < 768) {
        // 移动端：默认收起侧栏
        this.sidebarExpanded = false;
      } else {
        // 桌面端：默认展开侧栏
        this.sidebarExpanded = true;
      }
    },
    // 切换侧栏展开状态
    toggleSidebar() {
      this.sidebarExpanded = !this.sidebarExpanded;
    },
    // 处理导航点击事件 - 移动端自动收起侧边栏
    handleNavigationClick() {
      if (this.isMobile) {
        this.toggleSidebar();
      }
    },
    // 切换深色/浅色模式
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      // 保存模式到localStorage
      localStorage.setItem('darkMode', this.darkMode);
      // 触发自定义事件，让其他组件知道深色模式已切换
      window.dispatchEvent(new CustomEvent('darkModeChanged', { detail: { darkMode: this.darkMode } }));
    },
    // 切换直播状态的方法，供子组件调用
    toggleLiveStatus() {
      this.isLive = !this.isLive;
    },
    // 设置直播状态的方法
    setLiveStatus(status) {
      this.isLive = status;
    },
    /**
     * 检查认证状态
     */
    async checkAuthStatus() {
      try {
        const isLoggedIn = await authService.checkLoginStatus();
        if (!isLoggedIn) {
          // 如果未登录，跳转到登录页
          authService.redirectToLogin();
        }
      } catch (error) {
        console.error('认证状态检查失败:', error);
        // 如果检查失败，也跳转到登录页
        authService.redirectToLogin();
      }
    },
    /**
     * 处理退出登录
     */
    async handleLogout() {
      try {
        await authService.logout();
      } catch (error) {
        console.error('退出登录失败:', error);
        // 即使退出失败也重定向到首页
        window.location.href = window.location.origin;
      }
    },
    
    /**
     * 更新直播会话数据 - 供子组件调用
     */
    updateLiveSessionData(sessionData) {
      console.log('App.vue: 接收到直播会话数据:', sessionData);
      this.liveSessionData = sessionData;
      
      // 如果有直播会话数据，更新直播状态为true
      if (sessionData && sessionData.data && Array.isArray(sessionData.data) && sessionData.data.length > 0) {
        this.isLive = true;
        
        // 获取第一条直播会话数据
        const latestSession = sessionData.data[0];
        console.log('最新直播会话数据:', latestSession);
        
        // 更新直播数据
        if (latestSession.roomId) {
          this.liveStreamTitle = `Room: ${latestSession.roomId}`;
        }
        
        // 格式化在线用户数
        if (latestSession.onlineUsers !== undefined) {
          this.liveStreamViewers = this.formatNumber(latestSession.onlineUsers);
        }
        
        // 格式化点赞数
        if (latestSession.likeCount !== undefined) {
          this.liveStreamLikes = this.formatNumber(latestSession.likeCount);
        }
        
        // 格式化评论数
        if (latestSession.totalComments !== undefined) {
          this.liveStreamComments = this.formatNumber(latestSession.totalComments);
        }
        
        // 格式化礼物收入（假设totalGiftValue是收入，单位是分，需要转换为元）
        if (latestSession.totalGiftValue !== undefined) {
          // this.liveStreamRevenue = this.formatCurrency(latestSession.totalGiftValue);
          this.liveStreamRevenue = latestSession.totalGiftValue
        }
        
        // updateLiveSessionData方法只更新其他数据，不处理直播时长
        // 直播时长由checkSelectedStreamerLiveStatus方法中的startLiveDurationTimer处理
        console.log('updateLiveSessionData: 只更新其他数据，跳过时长计算');
        
        // 如果有唯一评论者数据，可以显示在副标题
        if (latestSession.uniqueCommenters !== undefined) {
          console.log('唯一评论者:', latestSession.uniqueCommenters);
        }
        
        // 如果有礼物数量数据，可以显示在副标题
        if (latestSession.totalGiftQuantity !== undefined) {
          console.log('礼物数量:', latestSession.totalGiftQuantity);
        }
        
      } else {
        // 如果没有直播会话数据，设置直播状态为false
        this.isLive = false;
        
        // 清除时长递增定时器
        if (this.durationInterval) {
          clearInterval(this.durationInterval);
          this.durationInterval = null;
        }
      }
    },
    
    /**
     * 只更新直播统计数据，不改变直播状态 - 用于定时更新
     * 使用非响应式更新，避免触发Dashboard.vue重新渲染
     */
    updateLiveStatsOnly(sessionData) {
      console.log('App.vue: 定时更新直播统计数据（非响应式）:', sessionData);
      
      // 只更新统计数据，不改变直播状态
      if (sessionData && sessionData.data && Array.isArray(sessionData.data) && sessionData.data.length > 0) {
        const latestSession = sessionData.data[0];
        console.log('定时更新最新直播数据（非响应式）:', latestSession);
        
        // 使用非响应式方式更新数据，避免触发Dashboard.vue重新渲染
        // 直接修改对象的属性，而不是重新赋值
        if (latestSession.roomId) {
          // 使用Vue.set来避免响应式更新触发重新渲染
          this.$set(this, 'liveStreamTitle', `Room: ${latestSession.roomId}`);
        }
        
        // 格式化在线用户数
        if (latestSession.onlineUsers !== undefined) {
          this.$set(this, 'liveStreamViewers', this.formatNumber(latestSession.onlineUsers));
        }
        
        // 格式化点赞数
        if (latestSession.likeCount !== undefined) {
          this.$set(this, 'liveStreamLikes', this.formatNumber(latestSession.likeCount));
        }
        
        // 格式化评论数
        if (latestSession.totalComments !== undefined) {
          this.$set(this, 'liveStreamComments', this.formatNumber(latestSession.totalComments));
        }
        
        // 格式化礼物收入
        if (latestSession.totalGiftValue !== undefined) {
          this.$set(this, 'liveStreamRevenue', latestSession.totalGiftValue);
        }
        
        console.log('定时更新完成（非响应式），只更新统计数据，不改变直播状态');
      }
    },
    
    
    /**
     * 格式化时长（秒转换为时分秒）
     */
    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
    },

    /**
     * 获取账户权益信息
     */
    async fetchAccountBenefitInfo() {
      try {
        this.benefitLoading = true;
        
        // 开发环境模拟数据 - 但为了测试，我们暂时注释掉开发环境的模拟数据
        // 这样在开发环境也会发起真实请求
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('开发环境：模拟账户权益信息');
        //   // 模拟接口返回数据
        //   this.accountBenefitInfo = {
        //     success: true,
        //     errCode: null,
        //     errMessage: null,
        //     data: {
        //       id: 2,
        //       accountSn: 267108798,
        //       creatorId: 1000306,
        //       bindingStatus: 3,
        //       verificationStatus: 0,
        //       verificationMethod: "manual",
        //       verificationCode: null,
        //       verificationLinkToken: null,
        //       verificationCodeExpireTime: null,
        //       verificationLinkExpireTime: null,
        //       verifiedAt: null,
        //       verifiedBy: null,
        //       isPrimary: false,
        //       bindingSource: "web",
        //       remark: null,
        //       createTime: "2025-11-21 00:21:55",
        //       updateTime: "2025-11-21 00:21:55"
        //     }
        //   };
        //   this.benefitLoading = false;
        //   return;
        // }

        // 调用真实接口（包括开发环境）
        const kssoUrl = process.env.VUE_APP_KSSO_URL || 'https://gateway.keco.vip';
        
        // 强制获取真实token，即使在开发环境
        let token;
        if (process.env.NODE_ENV === 'development') {
          // 开发环境下，优先尝试获取真实token
          token = localStorage.getItem('katoken') || authService.getToken();
          console.log('开发环境：强制使用真实token');
        } else {
          token = authService.getToken();
        }
        
        // 详细检查token
        console.log('检查用户token:', token);
        console.log('token类型:', typeof token);
        console.log('token长度:', token ? token.length : 0);
        console.log('token来源:', process.env.NODE_ENV === 'development' ? '开发环境' : '生产环境');
        
        if (!token) {
          console.warn('用户未登录，无法获取账户权益信息');
          this.benefitLoading = false;
          return;
        }
        
        // 不再检查测试token，直接使用获取到的token

        // 获取应用ID和密钥
        const appId = this.getAppId();
        const appSecret = this.getAppSecret();
        
        console.log('应用ID:', appId);
        console.log('应用密钥:', appSecret ? '***' + appSecret.slice(-4) : 'null');

        // 准备请求头
        const headers = {
          'req_app_id': appId,
          'req_app_secret': appSecret,
          'katoken': token,
          'Accept': '*/*',
          'Connection': 'keep-alive'
        };

        console.log('开始请求账户权益信息...');
        console.log('请求URL:', getApiUrl('USER_BENEFIT_INFO'));
        console.log('请求头详情:');
        console.log('- req_app_id:', headers['req_app_id']);
        console.log('- req_app_secret:', headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null');
        console.log('- katoken:', headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null');
        console.log('- Accept:', headers['Accept']);
        console.log('- Connection:', headers['Connection']);

        const response = await fetch(getApiUrl('USER_BENEFIT_INFO'), {
          method: 'GET',
          headers: headers
        });

        console.log('请求响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`获取账户权益信息失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        this.accountBenefitInfo = result;
        
        console.log('账户权益信息获取成功:', result);
        
      } catch (error) {
        console.error('获取账户权益信息失败:', error);
        this.accountBenefitInfo = null;
      } finally {
        this.benefitLoading = false;
      }
    },

    /**
     * 获取应用ID
     */
    getAppId() {
      // 使用与其他接口相同的应用ID
      return 'tiktok_monitor_app';
    },

    /**
     * 获取应用密钥
     */
    getAppSecret() {
      // 使用与其他接口相同的应用密钥
      return 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy';
    },

    /**
     * 获取绑定状态文本
     */
    getBindingStatusText(status) {
      const statusMap = {
        0: this.$t('app.bindingStatus.unbound'),
        1: this.$t('app.bindingStatus.bound'),
        2: this.$t('app.bindingStatus.unbinding'),
        3: this.$t('app.bindingStatus.pending'),
        4: this.$t('app.bindingStatus.unbindingInProgress'),
        5: this.$t('app.bindingStatus.unbindFailed')
      };
      return statusMap[status] || this.$t('app.unknownStatus');
    },

    /**
     * 获取验证状态文本
     */
    getVerificationStatusText(status) {
      const statusMap = {
        0: this.$t('app.verificationStatus.unverified'),
        1: this.$t('app.verificationStatus.verifying'),
        2: this.$t('app.verificationStatus.failed'),
        3: this.$t('app.verificationStatus.verified')
      };
      return statusMap[status] || this.$t('app.unknownStatus');
    },

    /**
     * 格式化日期
     */
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('日期格式化失败:', error);
        return dateString;
      }
    },

    /**
     * 格式化时长（秒转换为小时）
     */
    formatDurationHours(seconds) {
      const hours = seconds / 3600;
      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const remainingHours = Math.floor(hours % 24);
        return `${days}d ${remainingHours}h`;
      } else if (hours >= 1) {
        return `${Math.floor(hours)}h`;
      } else {
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m`;
      }
    },

    /**
     * 计算时长剩余百分比
     */
    getDurationPercentage(durationBenefit) {
      if (!durationBenefit || durationBenefit.availableDuration === 0) {
        return 0;
      }
      // 根据剩余时长计算剩余百分比
      const remainingPercentage = (durationBenefit.remainingDuration / durationBenefit.availableDuration) * 100;
      return Math.max(0, Math.min(100, remainingPercentage));
    },

    /**
     * 格式化token数量
     */
    formatTokens(tokens) {
      if (tokens >= 1000000) {
        return (tokens / 1000000).toFixed(1) + 'M';
      } else if (tokens >= 1000) {
        return (tokens / 1000).toFixed(1) + 'K';
      }
      return tokens.toString();
    },

    /**
     * 计算token剩余百分比
     */
    getTokenPercentage(tokenBenefit) {
      if (!tokenBenefit || tokenBenefit.availableTokens === 0) {
        return 0;
      }
      // 根据剩余token计算剩余百分比
      const remainingPercentage = (tokenBenefit.remainingTokens / tokenBenefit.availableTokens) * 100;
      return Math.max(0, Math.min(100, remainingPercentage));
    },

    /**
     * 格式化数字（添加千分位分隔符）
     */
    formatNumber(num) {
      if (num === undefined || num === null) return '0';
      if (typeof num === 'string') num = parseFloat(num);
      if (isNaN(num)) return '0';
      
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    },

    /**
     * 格式化货币（分转换为元）
     */
    formatCurrency(cents) {
      if (cents === undefined || cents === null) return '0.00';
      if (typeof cents === 'string') cents = parseFloat(cents);
      if (isNaN(cents)) return '0.00';
      
      const dollars = cents / 100;
      return dollars.toFixed(2);
    },

    /**
     * 选择主播
     */
    async selectStreamer(streamer) {
      this.currentStreamerId = streamer.id;
      this.streamerName = streamer.name;
      this.streamerAvatar = streamer.avatar;

        // 更新直播标题为直播间ID
        if (streamer.roomId) {
          this.liveStreamTitle = `${this.$t('app.room')}: ${streamer.roomId}`;
        }

      // 根据主播状态更新直播状态
      this.isLive = streamer.status === 'Live on TikTok';

      // 保存当前主播信息到缓存
      this.saveCurrentStreamerToCache(streamer);

      // 清理Dashboard相关的缓存数据
      this.cleanDashboardCache();

      // 关闭主播选择器
      this.showStreamerSelector = false;

      console.log(`切换到主播: ${streamer.name}, 直播间ID: ${streamer.roomId}`);

      // 立即查询该主播的直播间直播状态
      this.checkSelectedStreamerLiveStatus(streamer);

      // 通知Dashboard组件重新初始化数据
      this.notifyDashboardStreamerChanged(streamer);

      // 查询创作者详细信息（如果roomId存在）
      if (streamer.roomId) {
        try {
          const creatorInfo = await this.queryCreatorInfo(streamer.roomId);
          if (creatorInfo && creatorInfo.success && creatorInfo.data && creatorInfo.data.length > 0) {
            const creatorData = creatorInfo.data[0];
            console.log('获取到创作者详细信息:', creatorData);
            
            // 可以在这里处理创作者详细信息，例如更新头像、昵称等
            // 如果接口返回的头像更准确，可以更新头像
            if (creatorData.avatarUrl) {
              this.streamerAvatar = creatorData.avatarUrl;
            }
            
            // 更新昵称
            if (creatorData.nickname) {
              this.streamerName = creatorData.nickname;
            }
            
            // 可以保存创作者信息到缓存，供其他组件使用
            localStorage.setItem('creatorInfo', JSON.stringify(creatorData));
          }
        } catch (error) {
          console.error('获取创作者信息失败:', error);
        }
      }
    },

    /**
     * 通知Dashboard组件主播已切换
     */
    notifyDashboardStreamerChanged(streamer) {
      // 通过事件总线或直接调用Dashboard组件的方法来通知直播间切换
      // 这里使用事件总线方式，更符合Vue的设计模式
      if (this.$root && this.$root.$emit) {
        this.$root.$emit('streamer-changed', streamer);
      }
      
      // 同时保存新的直播间信息到localStorage，供Dashboard组件读取
      if (streamer.roomId) {
        localStorage.setItem('tiktok-live-room-id', streamer.roomId);
        console.log('已更新直播间ID到localStorage:', streamer.roomId);
      }
    },

    /**
     * 前往Onboarding页面添加新主播
     */
    goToOnboarding() {
      this.showStreamerSelector = false;
      this.$router.push('/onboarding');
    },

    /**
     * 打开主播选择器
     */
    async openStreamerSelector() {
      this.showStreamerSelector = true;
      // 打开模态框时自动获取主播列表
      await this.fetchStreamerList();
    },

    /**
     * 获取主播绑定列表（分页查询）
     */
    async fetchStreamerList(page = 1) {
      try {
        this.streamersLoading = true;
        
        // 获取token
        let token;
        if (process.env.NODE_ENV === 'development') {
          token = localStorage.getItem('katoken') || authService.getToken();
        } else {
          token = authService.getToken();
        }
        
        if (!token) {
          console.warn('用户未登录，无法获取主播列表');
          this.streamersLoading = false;
          return;
        }

        // 准备请求头
        const headers = {
          'req_app_id': this.getAppId(),
          'req_app_secret': this.getAppSecret(),
          'katoken': token,
          'Accept': '*/*',
          'Connection': 'keep-alive',
          'Content-Type': 'application/json'
        };

        // 准备请求体参数 - 使用分页参数
        const requestBody = {
          bindingStatus: 1, // 只查询已绑定的主播
          socialHandle: this.searchQuery, // 使用搜索关键词
          pageIndex: page,     // 当前页码
          pageSize: this.pagination.pageSize // 每页显示数量
        };

        console.log('开始请求主播绑定列表（分页查询）...');
        console.log('请求URL:', getApiUrl('USER_CREATOR_BINDING_PAGE'));
        console.log('请求参数:', requestBody);

        const response = await fetch(getApiUrl('USER_CREATOR_BINDING_PAGE'), {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(requestBody)
        });

        console.log('请求响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`获取主播绑定列表失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('主播绑定列表获取成功:', result);

        // 处理API响应数据
        if (result.success && result.data && Array.isArray(result.data)) {
          // 将API数据转换为前端需要的格式
          this.streamers = this.transformStreamerData(result.data);
          
          // 更新分页信息
          this.pagination.currentPage = page;
          this.pagination.total = result.totalCount || 0;
          this.pagination.totalPages = result.totalPages || Math.ceil(this.pagination.total / this.pagination.pageSize);
          
          // 如果有主播数据，默认选择第一个（只在第一页时）
          if (page === 1 && this.streamers.length > 0 && !this.currentStreamerId) {
            this.selectStreamer(this.streamers[0]);
          }
        } else {
          this.streamers = [];
          this.pagination.total = 0;
          this.pagination.totalPages = 0;
        }
        
      } catch (error) {
        console.error('获取主播绑定列表失败:', error);
        this.streamers = [];
        this.pagination.total = 0;
        this.pagination.totalPages = 0;
      } finally {
        this.streamersLoading = false;
      }
    },
    
    /**
     * 切换到指定页码
     */
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) {
        return;
      }
      this.fetchStreamerList(page);
    },
    
    /**
     * 搜索主播
     */
    searchStreamers() {
      // 重置到第一页进行搜索
      this.pagination.currentPage = 1;
      this.fetchStreamerList(1);
    },
    
    /**
     * 清除搜索
     */
    clearSearch() {
      this.searchQuery = '';
      this.pagination.currentPage = 1;
      this.fetchStreamerList(1);
    },
    
    /**
     * 获取可见的页码数组（用于分页组件显示）
     */
    getVisiblePages() {
      const totalPages = this.pagination.totalPages;
      const currentPage = this.pagination.currentPage;
      
      if (totalPages <= 7) {
        // 如果总页数小于等于7，显示所有页码
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      
      // 如果总页数大于7，显示部分页码
      let pages = [];
      
      if (currentPage <= 4) {
        // 当前页在前4页，显示前5页和最后2页
        pages = [1, 2, 3, 4, 5];
      } else if (currentPage >= totalPages - 3) {
        // 当前页在后4页，显示前2页和后5页
        pages = [1, 2];
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 当前页在中间，显示当前页前后各2页
        pages = [1, 2];
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          if (i > 2 && i < totalPages - 1) {
            pages.push(i);
          }
        }
        pages.push(totalPages - 1, totalPages);
      }
      
      // 去重并排序
      return [...new Set(pages)].sort((a, b) => a - b);
    },

    /**
     * 将API数据转换为前端需要的格式
     */
    transformStreamerData(apiData) {
      // 如果apiData是数组，直接处理
      if (Array.isArray(apiData)) {
        return apiData.map((item) => {
          // 从API数据中提取真实信息
          const username = item.socialHandle ? `@${item.socialHandle}` : null;
          const followers = item.creatorFollowerCount ? this.formatNumber(item.creatorFollowerCount) : '0';
          const bio = item.creatorBio || '';
          
          // 从简介中提取标签（如果有的话）
          const tags = this.extractTagsFromBio(bio);
          
          // 使用接口返回的lastSessionTimeAgo字段作为上次直播时间
          // 如果接口没有返回lastSessionTimeAgo，则使用lastSessionStartTime或updateTime
          let lastLive = 'Never';
          if (item.lastSessionTimeAgo) {
            lastLive = item.lastSessionTimeAgo;
          } else if (item.lastSessionStartTime) {
            lastLive = this.formatRelativeTime(item.lastSessionStartTime);
          } else if (item.updateTime) {
            lastLive = this.formatRelativeTime(item.updateTime);
          }
          
          // 使用接口返回的lastSessionDurationText字段作为上次直播时长
          // 如果接口没有返回lastSessionDurationText，则显示'0m'
          const duration = item.lastSessionDurationText || '0m';
          
          // 使用接口返回的isLive字段判断主播是否正在直播
          // 根据接口返回样例，isLive字段为布尔值：true表示正在直播，false表示不在直播
          const isLive = item.isLive === true;
          
          // 使用接口返回的videoCount字段作为视频数量
          // 如果接口没有返回videoCount，则显示'0'
          const videos = item.videoCount ? this.formatNumber(item.videoCount) : '0';
          
          return {
            id: item.id || item.creatorId,
            name: this.getStreamerName(item),
            avatar: this.getStreamerAvatar(item),
            status: this.getStreamerStatus(item),
            roomId: item.socialHandle || null, // 使用socialHandle作为roomId
            bindingStatus: item.bindingStatus,
            verificationStatus: item.verificationStatus,
            platform: item.platform || 'tiktok',
            // 使用API返回的真实数据
            username: username,
            isLive: isLive, // 使用接口返回的isLive字段
            followers: followers,
            videos: videos, // 使用接口返回的videoCount字段
            lastLive: lastLive, // 使用接口返回的上次直播时间
            duration: duration, // 使用接口返回的上次直播时长
            tags: tags,
            bio: bio,
            isVerified: item.creatorIsVerified || false,
            rawData: item // 保留原始数据
          };
        });
      }
      
      // 如果apiData是单个对象，包装成数组
      if (apiData && typeof apiData === 'object') {
        const username = apiData.socialHandle ? `@${apiData.socialHandle}` : null;
        const followers = apiData.creatorFollowerCount ? this.formatNumber(apiData.creatorFollowerCount) : '0';
        const bio = apiData.creatorBio || '';
        const tags = this.extractTagsFromBio(bio);
        
        // 使用接口返回的lastSessionTimeAgo字段作为上次直播时间
        let lastLive = 'Never';
        if (apiData.lastSessionTimeAgo) {
          lastLive = apiData.lastSessionTimeAgo;
        } else if (apiData.lastSessionStartTime) {
          lastLive = this.formatRelativeTime(apiData.lastSessionStartTime);
        } else if (apiData.updateTime) {
          lastLive = this.formatRelativeTime(apiData.updateTime);
        }
        
        // 使用接口返回的lastSessionDurationText字段作为上次直播时长
        const duration = apiData.lastSessionDurationText || '0m';
        
        // 使用接口返回的isLive字段判断主播是否正在直播
        const isLive = apiData.isLive === true;
        
        // 使用接口返回的videoCount字段作为视频数量
        // 如果接口没有返回videoCount，则显示'0'
        const videos = apiData.videoCount ? this.formatNumber(apiData.videoCount) : '0';
        
        return [{
          id: apiData.id || apiData.creatorId,
          name: this.getStreamerName(apiData),
          avatar: this.getStreamerAvatar(apiData),
          status: this.getStreamerStatus(apiData),
          roomId: apiData.socialHandle || null, // 使用socialHandle作为roomId
          bindingStatus: apiData.bindingStatus,
          verificationStatus: apiData.verificationStatus,
          platform: apiData.platform || 'tiktok',
          // 使用API返回的真实数据
          username: username,
          isLive: isLive, // 使用接口返回的isLive字段
          followers: followers,
          videos: videos, // 使用接口返回的videoCount字段
          lastLive: lastLive, // 使用接口返回的上次直播时间
          duration: duration, // 使用接口返回的上次直播时长
          tags: tags,
          bio: bio,
          isVerified: apiData.creatorIsVerified || false,
          rawData: apiData
        }];
      }
      
      return [];
    },
    
    /**
     * 从简介中提取标签
     */
    extractTagsFromBio(bio) {
      if (!bio) return [];
      
      // 常见的标签关键词
      const tagKeywords = [
        'GAMING', 'MUSIC', 'ART', 'DANCE', 'COMEDY', 'BEAUTY', 'FASHION',
        'FOOD', 'TRAVEL', 'FITNESS', 'EDUCATION', 'TECH', 'SPORTS', 'IRL',
        'JUST CHATTING', 'REACT', 'ASMR', 'COOKING', 'SINGING', 'DRAWING'
      ];
      
      const foundTags = [];
      const bioUpper = bio.toUpperCase();
      
      // 检查简介中是否包含标签关键词
      tagKeywords.forEach(tag => {
        if (bioUpper.includes(tag)) {
          foundTags.push(tag);
        }
      });
      
      // 如果没有找到标签，使用一些默认标签
      if (foundTags.length === 0) {
        // 根据简介内容推断可能的标签
        if (bioUpper.includes('GAME') || bioUpper.includes('PLAY')) {
          foundTags.push('GAMING');
        }
        if (bioUpper.includes('MUSIC') || bioUpper.includes('SONG') || bioUpper.includes('SING')) {
          foundTags.push('MUSIC');
        }
        if (bioUpper.includes('ART') || bioUpper.includes('DRAW') || bioUpper.includes('PAINT')) {
          foundTags.push('ART');
        }
        if (bioUpper.includes('CHAT') || bioUpper.includes('TALK')) {
          foundTags.push('IRL');
        }
      }
      
      // 限制最多3个标签
      return foundTags.slice(0, 3);
    },
    
    /**
     * 格式化相对时间（例如：2小时前，3天前）
     */
    formatRelativeTime(dateString) {
      if (!dateString) return this.$t('app.never');
      
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        
        if (diffDay > 0) {
          return `${diffDay}${this.$t('app.relativeTime.daysAgo')}`;
        } else if (diffHour > 0) {
          return `${diffHour}${this.$t('app.relativeTime.hoursAgo')}`;
        } else if (diffMin > 0) {
          return `${diffMin}${this.$t('app.relativeTime.minutesAgo')}`;
        } else {
          return this.$t('app.relativeTime.justNow');
        }
      } catch (error) {
        console.error('格式化相对时间失败:', error);
        return dateString;
      }
    },

    /**
     * 获取主播名称
     */
    getStreamerName(streamerData) {
      // 优先使用API返回的昵称字段
      if (streamerData.creatorNickname) {
        return streamerData.creatorNickname;
      }
      // 其次使用socialHandle作为主播名称
      if (streamerData.socialHandle) {
        return streamerData.socialHandle;
      }
      return this.$t('app.unknownStreamer');
    },

    /**
     * 获取主播头像
     */
    getStreamerAvatar(streamerData) {
      // 优先使用API返回的头像URL
      if (streamerData.creatorAvatarUrl) {
        return streamerData.creatorAvatarUrl;
      }
      // 其次使用旧的avatarUrl字段（向后兼容）
      if (streamerData.avatarUrl) {
        return streamerData.avatarUrl;
      }
      // 使用默认头像图片
      return '/avatar_default.png';
    },

    /**
     * 获取主播状态
     */
    getStreamerStatus(streamerData) {
      // 根据绑定状态确定主播状态
      if (streamerData.bindingStatus === 3) {
        return this.$t('app.streamerStatus.bindingReview');
      } else if (streamerData.bindingStatus === 1) {
        // 已绑定状态，根据验证状态显示不同文本
        if (streamerData.verificationStatus === 1) {
          return this.$t('app.bindingStatus.bound');
        } else if (streamerData.verificationStatus === 3) {
          return this.$t('app.liveOnTikTok');
        } else {
          return this.$t('app.bindingStatus.bound');
        }
      } else if (streamerData.bindingStatus === 2) {
        return this.$t('app.bindingStatus.unbinding');
      } else {
        return this.$t('app.unknownStatus');
      }
    },

    /**
     * 检查主播是否可点击
     * 只有 bindingStatus: 1 且 verificationStatus: 1 的主播才可以点击
     */
    isStreamerClickable(streamer) {
      return streamer.bindingStatus === 1 && streamer.verificationStatus === 1;
    },
    /**
     * 从缓存加载当前主播信息
     */
    async loadCurrentStreamerFromCache() {
      try {
        // 从localStorage获取当前主播信息
        const cachedStreamer = localStorage.getItem('currentStreamer');

        if (cachedStreamer) {
          const streamer = JSON.parse(cachedStreamer);
          console.log('从缓存加载当前主播信息:', streamer);

          // 检查缓存的主播信息是否有必要的字段
          // 旧版本的缓存可能没有bindingStatus和verificationStatus字段
          // 所以我们需要更宽松地检查
          if (!streamer.id || !streamer.name) {
            console.warn('缓存的主播信息缺少必要字段，不加载缓存信息:', streamer);
            
            // 清除无效的缓存
            localStorage.removeItem('currentStreamer');
            console.log('已清除无效的主播缓存信息');
            
            // 跳转到Onboarding页面
            this.redirectToOnboarding();
            return;
          }

          // 更新当前主播信息
          this.currentStreamerId = streamer.id;
          this.streamerName = streamer.name;
          this.streamerAvatar = streamer.avatar;

          // 更新直播标题为直播间ID
          if (streamer.roomId) {
            this.liveStreamTitle = `Room: ${streamer.roomId}`;
          }

          // 根据主播状态更新直播状态
          // 如果缓存中没有status字段，默认为false
          this.isLive = streamer.status === 'Live on TikTok';

          console.log(`从缓存恢复主播: ${streamer.name}, 直播间ID: ${streamer.roomId}`);

          // 从缓存加载完成后，立即检查该主播的实时直播状态
          this.checkSelectedStreamerLiveStatus(streamer);

          // 查询创作者详细信息（如果roomId存在）以获取最新头像
          if (streamer.roomId) {
            try {
              const creatorInfo = await this.queryCreatorInfo(streamer.roomId);
              if (creatorInfo && creatorInfo.success && creatorInfo.data && creatorInfo.data.length > 0) {
                const creatorData = creatorInfo.data[0];
                console.log('从缓存加载时获取到创作者详细信息:', creatorData);
                
                // 更新头像信息
                if (creatorData.avatarUrl) {
                  this.streamerAvatar = creatorData.avatarUrl;
                  console.log('已更新头像为:', creatorData.avatarUrl);
                }
              }
            } catch (error) {
              console.error('从缓存加载时获取创作者信息失败:', error);
            }
          }
        } else {
          console.log('缓存中没有主播信息，跳转到Onboarding页面');
          // 跳转到Onboarding页面
          this.redirectToOnboarding();
        }
      } catch (error) {
        console.error('从缓存加载主播信息失败:', error);
        // 出错时也跳转到Onboarding页面
        this.redirectToOnboarding();
      }
    },

    /**
     * 重置主播信息并保持在当前页面
     */
    redirectToOnboarding() {
      console.log('重置主播信息，保持在当前页面');
      // 清除当前主播信息
      this.currentStreamerId = null;
      this.streamerName = '';
      this.streamerAvatar = '/avatar_default.png';
      this.isLive = false;
      
      // 清除缓存
      localStorage.removeItem('currentStreamer');
      
      // 不清除直播间ID，以便OnAir页面可以显示"Ready to go live!"状态
      // 注意：我们不跳转到Onboarding页面，而是保持在当前页面
      // OnAir.vue会根据hasRoomConnected和actualIsLive状态显示相应的界面
      console.log('主播信息已重置，保持在当前页面');
    },

    /**
     * 保存当前主播信息到缓存
     */
    saveCurrentStreamerToCache(streamer) {
      try {
        const streamerData = {
          id: streamer.id,
          name: streamer.name,
          avatar: streamer.avatar,
          status: streamer.status,
          roomId: streamer.roomId
        };
        
        localStorage.setItem('currentStreamer', JSON.stringify(streamerData));
        console.log('当前主播信息已保存到缓存:', streamerData);
      } catch (error) {
        console.error('保存主播信息到缓存失败:', error);
      }
    },

    /**
     * 清理Dashboard相关的缓存数据
     */
    cleanDashboardCache() {
      try {
        console.log('开始清理Dashboard相关缓存数据...');
        
        // 清理Dashboard页面可能存储的缓存数据
        const dashboardCacheKeys = [
          'tiktok-live-room-id',
          'tiktok-binding-info',
          'dashboard-activities',
          'dashboard-historical-comments',
          'dashboard-potential-patrons',
          'dashboard-last-comment-time',
          'dashboard-suggestions',
          'dashboard-current-suggestion-index',
          'dashboard-showing-single-suggestion',
          'dashboard-pending-batch-suggestion'
        ];
        
        dashboardCacheKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log(`已清理缓存: ${key}`);
          }
        });
        
        // 清理可能存在的其他Dashboard相关缓存
        const allKeys = Object.keys(localStorage);
        const dashboardRelatedKeys = allKeys.filter(key => 
          key.startsWith('dashboard-') || 
          key.startsWith('tiktok-') ||
          key.includes('live') ||
          key.includes('streamer')
        );
        
        dashboardRelatedKeys.forEach(key => {
          if (!dashboardCacheKeys.includes(key)) {
            localStorage.removeItem(key);
            console.log(`已清理额外缓存: ${key}`);
          }
        });
        
        console.log('Dashboard缓存清理完成');
      } catch (error) {
        console.error('清理Dashboard缓存失败:', error);
      }
    },

    /**
     * 检查选中主播的直播间直播状态
     */
    async checkSelectedStreamerLiveStatus(streamer) {
      try {
        console.log(`开始查询主播 ${streamer.name} 的直播间直播状态，直播间ID: ${streamer.roomId}`);
        
        if (!streamer.roomId) {
          console.warn('主播没有有效的直播间ID，无法查询直播状态');
          return;
        }

        // 获取token
        let token;
        if (process.env.NODE_ENV === 'development') {
          token = localStorage.getItem('katoken') || authService.getToken();
        } else {
          token = authService.getToken();
        }

        if (!token) {
          console.warn('用户未登录，无法查询直播状态');
          return;
        }

        // 准备请求头
        const headers = {
          'req_app_id': this.getAppId(),
          'req_app_secret': this.getAppSecret(),
          'katoken': token
        };

        console.log('查询直播状态请求头:', {
          'req_app_id': headers['req_app_id'],
          'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
          'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
        });

        // 查询直播状态
        const response = await fetch(`${getApiUrl('LIVE_SESSIONS_ROOM_CURRENT')}/${streamer.roomId}/current`, {
          method: 'GET',
          headers: headers
        });

        console.log('直播状态查询响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`查询直播状态失败: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('直播状态查询结果:', data);

        // 分析API返回结果来判断直播间连接状态
        if (data.success && data.data && data.data.status === 1) {
          // 有直播正在进行中
          console.log(`主播 ${streamer.name} 正在直播中`);
          console.log('直播数据详情:', data.data);
          this.isLive = true;
          
          // 基于startTime计算直播时长
          console.log('开始调用startLiveDurationTimer，startTime:', data.data.startTime);
          console.log('startTime类型:', typeof data.data.startTime);
          console.log('startTime值:', data.data.startTime);
          
          // 确保startTime存在且有效
          if (data.data.startTime && data.data.startTime !== 'undefined' && data.data.startTime !== 'null') {
            this.startLiveDurationTimer(data.data.startTime);
          } else {
            console.warn('startTime无效，无法启动时长计时器:', data.data.startTime);
          }
          
          // 获取直播统计数据
          await this.fetchLiveSessionDataForSelectedStreamer(streamer.roomId, token);
          
          // 启动直播统计数据定时更新
          this.startLiveStatsTimer(streamer.roomId, token);
        } else if (data.errCode === '404') {
          // 房间存在但当前没有直播
          console.log(`主播 ${streamer.name} 当前没有直播`);
          this.isLive = false;
          
          // 清除时长递增定时器
          if (this.durationInterval) {
            clearInterval(this.durationInterval);
            this.durationInterval = null;
          }
          
          // 清除直播统计数据定时器
          this.stopLiveStatsTimer();
        } else {
          // 其他错误情况
          console.log(`主播 ${streamer.name} 直播状态查询失败:`, data);
          this.isLive = false;
          
          // 清除时长递增定时器
          if (this.durationInterval) {
            clearInterval(this.durationInterval);
            this.durationInterval = null;
          }
          
          // 清除直播统计数据定时器
          this.stopLiveStatsTimer();
        }

        console.log(`主播 ${streamer.name} 直播状态已更新:`, this.isLive);

      } catch (error) {
        console.error('查询选中主播直播状态失败:', error);
        this.isLive = false;
        
        // 清除时长递增定时器
        if (this.durationInterval) {
          clearInterval(this.durationInterval);
          this.durationInterval = null;
        }
        
        // 清除直播统计数据定时器
        this.stopLiveStatsTimer();
      }
    },

    /**
     * 获取选中主播的直播会话统计数据
     */
    async fetchLiveSessionDataForSelectedStreamer(roomId, katoken) {
      try {
        console.log('获取选中主播的直播会话数据，房间ID:', roomId);

        // 先获取当前直播的sessionId
        const currentResponse = await fetch(`${getApiUrl('LIVE_SESSIONS_ROOM_CURRENT')}/${roomId}/current`, {
          method: 'GET',
          headers: {
            'req_app_id': this.getAppId(),
            'req_app_secret': this.getAppSecret(),
            'katoken': katoken
          }
        });

        const currentData = await currentResponse.json();
        console.log('当前直播状态数据:', currentData);

        let currentSessionId = null;
        if (currentData.success && currentData.data && currentData.data.sessionId) {
          currentSessionId = currentData.data.sessionId;
          console.log('当前直播的sessionId:', currentSessionId);
        }

        // 如果没有获取到sessionId，显示数据统计中提示
        if (!currentSessionId) {
          console.log('未获取到当前直播的sessionId，显示数据统计中提示');
          this.showDataCollectingMessage();
          return;
        }

        // 使用新的接口获取单场统计数据
        const requestBody = {
          liveRoomId: roomId,
          sessionId: currentSessionId
        };

        console.log('请求直播场次详情统计，参数:', requestBody);

        const response = await fetch(getApiUrl('LIVE_SESSION_STATS_DETAIL'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': this.getAppId(),
            'req_app_secret': this.getAppSecret(),
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();
        console.log('直播场次详情统计数据:', data);

        if (data.success && data.data) {
          // 将数据格式转换为与原有接口兼容的格式
          const formattedData = {
            success: true,
            errCode: null,
            errMessage: null,
            data: [{
              sessionId: (data.data.sessionInfo && data.data.sessionInfo.sessionId) || currentSessionId,
              roomId: roomId,
              title: (data.data.sessionInfo && data.data.sessionInfo.title) || '',
              coverUrl: (data.data.sessionInfo && data.data.sessionInfo.coverUrl) || '',
              startTime: (data.data.sessionInfo && data.data.sessionInfo.startTime) || '',
              endTime: (data.data.sessionInfo && data.data.sessionInfo.endTime) || '',
              status: (data.data.sessionInfo && data.data.sessionInfo.status) || 0,
              statusDesc: (data.data.sessionInfo && data.data.sessionInfo.statusDesc) || '',
              totalViewers: (data.data.sessionInfo && data.data.sessionInfo.totalViewers) || 0,
              peakViewers: (data.data.sessionInfo && data.data.sessionInfo.peakViewers) || 0,
              duration: (data.data.sessionInfo && data.data.sessionInfo.duration) || 0,
              // 从messageTypeStats中提取评论数量
              totalComments: this.extractCommentCountFromMessageTypeStats(data.data.messageTypeStats),
              // 从onlineStats中提取点赞数
              likeCount: (data.data.onlineStats && data.data.onlineStats.maxLikeCount) || 0,
              // 从giftStats中提取礼物收入
              totalGiftValue: (data.data.giftStats && data.data.giftStats.totalGiftValue) || 0,
              totalGiftQuantity: (data.data.giftStats && data.data.giftStats.totalGiftNum) || 0,
              // 从onlineStats中提取在线用户数
              onlineUsers: (data.data.onlineStats && data.data.onlineStats.maxOnlineUsers) || 0
            }]
          };

          console.log('格式化后的直播数据:', formattedData);
          // 使用新的方法只更新统计数据，不改变直播状态
          this.updateLiveStatsOnly(formattedData);
        } else {
          console.warn('获取直播场次详情统计失败或无数据:', data);
          this.showDataCollectingMessage();
        }
      } catch (error) {
        console.error('获取选中主播直播会话数据失败:', error);
        // 出错时显示"数据正在统计中"
        this.showDataCollectingMessage();
      }
    },

    /**
     * 从messageTypeStats中提取评论数量
     */
    extractCommentCountFromMessageTypeStats(messageTypeStats) {
      if (!messageTypeStats || !Array.isArray(messageTypeStats)) {
        return 0;
      }
      
      const chatStat = messageTypeStats.find(stat => stat.messageType === 'chat');
      return chatStat ? chatStat.count : 0;
    },

    /**
     * 显示数据正在统计中的提示
     */
    showDataCollectingMessage() {
      console.log('显示数据正在统计中提示');
      
      // 设置直播数据为"数据正在统计中"状态
      this.liveStreamViewers = this.$t('app.collecting');
      this.liveStreamLikes = this.$t('app.collecting');
      this.liveStreamComments = this.$t('app.collecting');
      this.liveStreamRevenue = this.$t('app.collecting');
      
      // 可以添加一个定时器，在几分钟后重新尝试获取数据
      setTimeout(() => {
        console.log('重新尝试获取直播统计数据');
        this.checkSelectedStreamerLiveStatus({ roomId: this.getCurrentRoomId() });
      }, 60000); // 1分钟后重试
    },

    /**
     * 获取当前直播间ID
     */
    getCurrentRoomId() {
      // 从当前主播信息或缓存中获取直播间ID
      const cachedStreamer = localStorage.getItem('currentStreamer');
      if (cachedStreamer) {
        try {
          const streamer = JSON.parse(cachedStreamer);
          return streamer.roomId;
        } catch (error) {
          console.error('解析当前主播信息失败:', error);
        }
      }
      return null;
    },

    /**
     * 基于直播开始时间启动时长计时器
     */
    startLiveDurationTimer(startTime) {
      console.log('startLiveDurationTimer被调用，startTime:', startTime);

      if (!startTime || startTime === 'null' || startTime === 'undefined') {
        console.warn('没有有效的直播开始时间，无法启动时长计时器，startTime值:', startTime);
        return;
      }

      // 清除之前的定时器
      if (this.durationInterval) {
        clearInterval(this.durationInterval);
        this.durationInterval = null;
      }

      try {
        // 判断startTime格式，如果是ISO字符串或者UTC时间，则需要特殊处理
        let startDate;

        if (startTime.includes('T') && startTime.includes('Z')) {
          // 处理ISO字符串格式，如 "2025-11-27T13:21:50.000Z"
          console.log('检测到ISO时间字符串格式');
          startDate = new Date(startTime);
        } else if (startTime.includes('T')) {
          // 处理ISO字符串但没有Z后缀的情况
          console.log('检测到ISO时间字符串格式（无时区）');
          startDate = new Date(startTime + (startTime.includes('.') ? '' : '.000') + 'Z');
        } else {
          // 处理本地时间字符串格式: "2025-11-27 13:21:50"
          console.log('检测到本地时间字符串格式:', startTime);
          const [datePart, timePart] = startTime.split(' ');
          if (!datePart || !timePart) {
            throw new Error('时间字符串格式不正确');
          }

          const [year, month, day] = datePart.split('-').map(Number);
          const [hour, minute, second] = timePart.split(':').map(Number);

          console.log('解析结果:', { year, month, day, hour, minute, second });

          // 创建本地时间对象（假设服务器返回的是本地时间字符串，不包含时区信息）
          // 直接使用本地时间构造函数，让JavaScript自动处理时区
          startDate = new Date(year, month - 1, day, hour, minute, second);
        }

        const currentDate = new Date();

        console.log('时间对象信息:', {
          startTime原始值: startTime,
          startDate: startDate.toString(),
          startDateISO: startDate.toISOString(),
          startDateTimestamp: startDate.getTime(),
          currentDate: currentDate.toString(),
          currentDateISO: currentDate.toISOString(),
          currentDateTimestamp: currentDate.getTime()
        });

        // 计算从开始时间到现在的时长（秒）
        const startTimestamp = startDate.getTime();
        const currentTimestamp = currentDate.getTime();
        const durationDiff = Math.floor((currentTimestamp - startTimestamp) / 1000);

        console.log('时长计算详情:', {
          startTimestamp,
          currentTimestamp,
          timeDiffMs: currentTimestamp - startTimestamp,
          durationDiffSeconds: durationDiff
        });

        // 设置初始时长，确保不为负数
        this.currentLiveDuration = Math.max(0, durationDiff);
        this.liveStreamDuration = this.formatDuration(this.currentLiveDuration);

        console.log(`直播时长已计算: 开始时间=${startTime}, 解析后=${startDate.toISOString()}, 当前时间=${currentDate.toISOString()}, 时长=${this.currentLiveDuration}秒, 格式化=${this.liveStreamDuration}`);

        // 强制更新视图，确保UI能正确显示时长
        this.$nextTick(() => {
          console.log(`视图已更新，时长应显示为: ${this.liveStreamDuration}`);
        });

        // 每秒递增一次直播时长
        this.durationInterval = setInterval(() => {
          this.currentLiveDuration += 1;
          this.liveStreamDuration = this.formatDuration(this.currentLiveDuration);
          // 减少日志输出，只在关键时刻输出
          if (this.currentLiveDuration % 60 === 0) { // 每分钟输出一次日志
            console.log('直播时长更新:', this.liveStreamDuration);
          }
        }, 1000);

        console.log('直播时长计时器已启动');

      } catch (error) {
        console.error('解析直播开始时间失败:', error);
        console.error('startTime原始值:', startTime);
        console.error('错误详情:', error.message);

        // 如果解析失败，重置时长显示
        this.liveStreamDuration = '00:00:00';
        this.currentLiveDuration = 0;
      }
    },

    /**
     * 启动直播统计数据定时更新
     */
    startLiveStatsTimer(roomId, token) {
      console.log('启动直播统计数据定时更新，房间ID:', roomId);
      
      // 先清除可能存在的旧定时器
      this.stopLiveStatsTimer();
      
      // 立即执行一次获取直播统计数据
      this.fetchLiveSessionDataForSelectedStreamer(roomId, token);
      
      // 设置定时器，每隔1分钟（60000毫秒）获取一次直播统计数据
      this.liveStatsInterval = setInterval(() => {
        console.log('定时器触发：获取直播统计数据');
        this.fetchLiveSessionDataForSelectedStreamer(roomId, token);
      }, 60000); // 1分钟 = 60000毫秒
      
      console.log('直播统计数据定时器已启动，间隔：1分钟');
    },

    /**
     * 停止直播统计数据定时更新
     */
    stopLiveStatsTimer() {
      console.log('停止直播统计数据定时更新');
      if (this.liveStatsInterval) {
        clearInterval(this.liveStatsInterval);
        this.liveStatsInterval = null;
        console.log('直播统计数据定时器已停止');
      }
    },

    /**
     * 初始化深色/浅色模式
     */
    initDarkMode() {
      // 从localStorage读取用户选择的模式
      const savedMode = localStorage.getItem('darkMode');
      console.log('App.vue: initDarkMode读取到的savedMode:', savedMode, '类型:', typeof savedMode);
      
      if (savedMode === 'light') {
        // 用户选择浅色模式
        this.darkMode = false;
        console.log('App.vue: 设置为浅色模式');
      } else if (savedMode === 'dark') {
        // 用户选择深色模式
        this.darkMode = true;
        console.log('App.vue: 设置为深色模式');
      } else if (savedMode === 'true' || savedMode === true) {
        // 兼容旧版本：保存的是布尔值true
        this.darkMode = true;
        console.log('App.vue: 兼容旧版本，设置为深色模式（布尔值true）');
      } else if (savedMode === 'false' || savedMode === false) {
        // 兼容旧版本：保存的是布尔值false
        this.darkMode = false;
        console.log('App.vue: 兼容旧版本，设置为浅色模式（布尔值false）');
      } else {
        // 用户选择跟随系统或未设置，检测系统偏好
        this.darkMode = this.detectSystemDarkMode();
        console.log('App.vue: 设置为跟随系统模式，检测结果:', this.darkMode);
      }
      
      // 应用模式
      this.applyDarkMode();
    },
    
    /**
     * 检测系统深色模式偏好
     */
    detectSystemDarkMode() {
      // 检查浏览器是否支持prefers-color-scheme
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
      return false;
    },
    
    /**
     * 应用深色/浅色模式
     */
    applyDarkMode() {
      // 更新HTML的class
      if (this.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      // 触发自定义事件，让其他组件知道深色模式已切换
      window.dispatchEvent(new CustomEvent('darkModeChanged', { detail: { darkMode: this.darkMode } }));
    },
    
    /**
     * 处理主题模式变化事件
     */
    handleThemeModeChanged(event) {
      console.log('App.vue: 收到themeModeChanged事件，mode:', event.detail.mode);
      this.setDarkMode(event.detail.mode);
    },
    
    /**
     * 设置深色/浅色模式
     * @param {string} mode - 'light'、'dark' 或 'system'
     */
    setDarkMode(mode) {
      console.log('App.vue: 设置主题模式:', mode);
      
      // 保存用户选择到localStorage
      localStorage.setItem('darkMode', mode);
      
      if (mode === 'light') {
        this.darkMode = false;
        console.log('App.vue: 应用浅色模式');
      } else if (mode === 'dark') {
        this.darkMode = true;
        console.log('App.vue: 应用深色模式');
      } else {
        // 跟随系统
        this.darkMode = this.detectSystemDarkMode();
        console.log('App.vue: 应用跟随系统模式，检测结果:', this.darkMode);
      }
      
      // 应用模式
      this.applyDarkMode();
    },
    
    /**
     * 获取当前模式设置
     */
    getCurrentMode() {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode === 'light' || savedMode === 'dark') {
        return savedMode;
      }
      return 'system';
    },

    /**
     * 查询创作者详细信息
     * @param {string|Array} usernames - 用户名（roomid）列表，可以是字符串（逗号分隔）或数组
     */
    async queryCreatorInfo(usernames) {
      try {
        console.log('开始查询创作者信息，usernames:', usernames);
        
        // 获取token
        let token;
        if (process.env.NODE_ENV === 'development') {
          token = localStorage.getItem('katoken') || authService.getToken();
        } else {
          token = authService.getToken();
        }
        
        if (!token) {
          console.warn('用户未登录，无法查询创作者信息');
          return null;
        }

        // 准备请求头 - 使用特定的app_id和app_secret
        const headers = {
          'req_app_id': 'agent_admin_web',
          'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
          'katoken': token
        };

        // 处理usernames参数
        let usernamesParam;
        if (Array.isArray(usernames)) {
          usernamesParam = usernames.join(',');
        } else {
          usernamesParam = usernames;
        }

        console.log('查询创作者信息请求参数:', {
          usernames: usernamesParam,
          headers: {
            'req_app_id': headers['req_app_id'],
            'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
            'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
          }
        });

        // 构建URL，将参数作为查询字符串
        const apiUrl = `${getApiUrl('CREATOR_QUERY')}?usernames=${encodeURIComponent(usernamesParam)}`;

        // 发送GET请求
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers
        });

        console.log('创作者信息查询响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`查询创作者信息失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('创作者信息查询结果:', result);
        
        return result;
        
      } catch (error) {
        console.error('查询创作者信息失败:', error);
        return null;
      }
    }
  },
  beforeDestroy() {
    // 组件销毁时清除定时器
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
    
    // 清除直播统计数据定时器
    this.stopLiveStatsTimer();
  },
  provide() {
    // 提供直播状态相关的方法和数据给子组件
    return {
      isLive: () => this.isLive, // 使用函数返回响应式值
      toggleLiveStatus: this.toggleLiveStatus,
      setLiveStatus: this.setLiveStatus,
      updateLiveSessionData: this.updateLiveSessionData,
      sidebarExpanded: () => this.sidebarExpanded, // 提供侧边栏展开状态
      liveStreamData: {
        streamerName: this.streamerName,
        streamerAvatar: this.streamerAvatar,
        liveStreamCover: this.liveStreamCover,
        liveStreamTitle: this.liveStreamTitle,
        liveStreamDuration: this.liveStreamDuration,
        liveStreamViewers: this.liveStreamViewers,
        liveStreamLikes: this.liveStreamLikes,
        liveStreamComments: this.liveStreamComments,
        liveStreamRevenue: this.liveStreamRevenue,
        remainingDuration: this.remainingDuration,
        remainingDurationPercentage: this.remainingDurationPercentage
      }
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Spline+Sans:wght@300..700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined');

/* Tailwind CSS 配置 */
@import url('https://cdn.tailwindcss.com?plugins=forms,container-queries');

/* 自定义样式 */
body {
  font-family: 'Spline Sans', sans-serif;
  background-color: #f6f6f8;
}

.dark body {
  background-color: #101622;
}

/* 确保路由链接样式正确 */
.router-link-active {
  background-color: #00D26A !important;
}

/* 自定义滚动条样式 */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #1e293b;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* 隐藏滚动条样式 */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}
</style>
